// The site inside the Sotravel app.
//
// The Expo app loads this site in webviews and, on every document load and
// before any page code runs, sets (sotravel-expo docs/webview-bridge.md):
//   window.isInApp = true    in every app webview
//   window.dedicatedScreen   true when the page is a native screen of its own
//                            (account pages, trip details, courses…), not a tab
//
// In the app there is no marketing site: "home" is the Explore feed
// (/all-trips), which every app container already treats as home (the Explore
// tab shows its tab bar there, the Profile tab switches to Explore, the Nearby
// trip screen pops). Older app builds mishandle new-window requests (iOS
// loads them in place, Android hands them to the system browser), so our own
// pages must never rely on target="_blank" / window.open there.

export const APP_HOME = "/all-trips";

export const isInApp = () => typeof window !== "undefined" && Boolean(window.isInApp);

// A native screen of its own, closed by the app on {type:"goBack"}. Decided
// only from the flag: a tab webview must never send "goBack" (the app's tab
// navigator would switch tabs).
export const isDedicatedScreen = () => isInApp() && window.dedicatedScreen === true;

// Nearby owns a native screen, but has NO native header. Older app builds
// also mark it dedicatedScreen; keep web headers visible in those builds.
export const hasNativeHeader = () => isDedicatedScreen() && !window.isNearbyWebview;

// Back that always lands somewhere. React Router keeps a growing `idx` in
// history.state for the entries it creates; 0 (or none) means this is the
// first page of the document — a fresh webview, a deep link, a page loaded
// from another site — where navigate(-1) leaves the site or does nothing.
// (history.length can't tell: it counts other documents and forward entries.)
// Then a dedicated app screen is closed, and the app goes to `fallback` or
// /all-trips. The website keeps its browser back while there is any history
// (a visitor may have come from another site) and only then falls back to
// `fallback` or /.
// Same rule as useAccountBack (pages/account/kit.js), Book.js handleBack and
// the SoFest / Signature navbars.
export const goBackSafe = (navigate, fallback) => {
  if (window.history.state?.idx > 0) {
    navigate(-1);
    return;
  }
  if (
    (isDedicatedScreen() || (isInApp() && window.isNearbyWebview)) &&
    window.ReactNativeWebView?.postMessage
  ) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "goBack" }));
    return;
  }
  if (!isInApp() && window.history.length > 1) {
    navigate(-1);
    return;
  }
  navigate(fallback ?? (isInApp() ? APP_HOME : "/"), { replace: true });
};

// Props for a link to one of our own pages that the website opens in a new
// tab. In the app that click would do nothing, so there the page opens in
// place through the router (and its back button returns here).
export const ownPageLinkProps = (navigate, path, rel = "noopener noreferrer") =>
  isInApp()
    ? {
        onClick: (event) => {
          event.preventDefault();
          navigate(path);
        },
      }
    : { target: "_blank", rel };

// Props that render a card as a real <a href> to one of our own pages, so
// crawlers can follow it and cmd/ctrl/shift- or middle-click open it like any
// link, while a plain click still runs the card's own handler (router state,
// analytics, new tab on desktop, in-app navigation). The default is prevented
// in the capture phase, so a control inside the card that stops propagation
// (carousel arrows, a dates button, an avatar) never follows the link either.
// Clicks from a dialog the card opened bubble through React but are not inside
// the link, so they are left alone.
export const cardLinkProps = (href, onSelect) => {
  const isPlainClickOnCard = (event) =>
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    event.currentTarget.contains(event.target);
  return {
    href,
    onClickCapture: (event) => {
      if (isPlainClickOnCard(event)) event.preventDefault();
    },
    onClick: (event) => {
      if (isPlainClickOnCard(event)) onSelect(event);
    },
  };
};
